<template>
    <div
        v-if="isOpen"
        ref="refModal"
        class="ui-modal"
    >
        <div class="ui-modal__overlay" @click="closeModal"></div>

        <div class="ui-modal__container">
            <button class="ui-modal__close-button" @click="closeModal">
                <slot name="close-icon">
                    <img v-svg-inline src="@/assets/icons/ui/modal-close.svg" />
                </slot>
            </button>

            <transition name="ui-modal-slide">
                <div v-show="showContent" class="ui-modal__content">
                    <slot></slot>
                </div>
            </transition>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { onMounted, onUnmounted, ref } from 'vue';

    defineProps({
        isOpen: Boolean,
    });

    const emit = defineEmits(['close']);

    const refModal = ref<HTMLElement | null>(null);
    const showContent = ref<boolean>(false);

    let scrollProcessed: boolean = true;
    let lastScrollPosition: number = 0;
    let scrollDirection: 'up' | 'down' = 'down';

    function closeModal(): void {
        emit('close');
    }

    function handleScroll(): void {
        if (!refModal.value) {
            return;
        }

        if (window.screen.width <= 768) {
            return;
        }

        scrollDirection = refModal.value.scrollTop > lastScrollPosition ? 'down' : 'up';
        lastScrollPosition = refModal.value.scrollTop;

        const isAtTop = refModal.value.scrollTop === 0;
        const isAtBottom = refModal.value.scrollHeight - refModal.value.scrollTop - refModal.value.clientHeight < 24;

        refModal.value.style.marginTop = isAtTop ? '24px' : '0';
        refModal.value.style.maxHeight = isAtBottom ? 'calc(100vh - 24px)' : '100vh';

        if (isAtBottom) {
            if (scrollProcessed && scrollDirection === 'down') {
                scrollProcessed = false;

                setTimeout(() => {
                    refModal.value?.scrollTo({
                        top: refModal.value.scrollHeight,
                        // behavior: 'smooth',
                    });

                    scrollProcessed = true;
                }, 20);
            }
        }
    }

    onMounted(() => {
        showContent.value = true;

        if (refModal.value) {
            refModal.value.addEventListener('scroll', handleScroll);

            document.documentElement.style.setProperty('overflow', 'hidden');
        }
    });

    onUnmounted(() => {
        if (refModal.value) {
            refModal.value.removeEventListener('scroll', handleScroll);
        }

        document.documentElement.style.removeProperty('overflow');
    });
</script>

<style scoped lang="scss">
    @use './UIModal.scss' as *;
</style>
